import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import ZipForm from '~components/ZipCodeForm/ZipCodeForm';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import CloseIcon from '~images/inline/close.inline.svg';
import { toggleStickyEmailGuide } from '~redux/Actions/DisplayActions';

type Props = {
    background?: string;
    useFlexRow?: boolean;
    isLeft?: boolean;
    shouldHide?: boolean;
    title: string;
    titleColor?: string;
    titleSizes?: string;
    zipContainerClasses?: string;
};

const FindPlan = ({
    background = 'bg-gray-lightest',
    useFlexRow = false,
    isLeft = false,
    shouldHide = false,
    title,
    titleColor = 'gray-dark',
    titleSizes = 'text-lg md:text-2xl',
    zipContainerClasses = '',
}: Props) => {
    const [currentScroll, setCurrentScroll] = useState(0);
    const [initialHeight, setInitialHeight] = useState(0);
    const [isDismissed, setIsDismissed] = useState(shouldHide);
    const [isPinned, setIsPinned] = useState(false);

    const pageData = usePageContext();

    const isExpanded = useAppSelector((state) => state.display.stickyGuideOpen);
    const isEnrollmentPage = pageData.location.pathname.includes('/enrollment/');

    const container = useRef(null as null | HTMLDivElement);

    const dispatch = useAppDispatch();

    const handleClose = () => {
        setIsPinned(false);
        setIsDismissed(true);
    };

    const waLink: string =
        'Click:' + process.env.GATSBY_ANALYTICS_SITE_GROUP + ':ReadytoFindaPlan:GetStarted';

    useEffect(() => {
        if (isPinned && isExpanded) {
            dispatch(toggleStickyEmailGuide(false));
        }
    }, [isPinned]);

    useEffect(() => {
        const onScroll = () => {
            const isScrolledPast =
                window.scrollY >=
                (container.current?.offsetTop as number) +
                    (container.current?.offsetHeight as number);
            const diyZipForm = document.querySelector('[data-qa="zip-form-LiveFindPlanNeedHelp"]');
            const hasReachedDiyZipForm =
                diyZipForm &&
                window.scrollY + window.innerHeight >= (diyZipForm as HTMLElement).offsetTop;

            setCurrentScroll(window.scrollY);
            if (isScrolledPast && !hasReachedDiyZipForm && !isPinned && !isDismissed) {
                setIsPinned(true);
            }
            if ((!isScrolledPast || hasReachedDiyZipForm) && isPinned) {
                setIsPinned(false);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [currentScroll]);

    useEffect(() => {
        setInitialHeight(container.current?.offsetHeight as number);
    }, []);

    return (
        <div style={{ minHeight: initialHeight }} ref={container}>
            <div
                className={classNames(
                    'z-20',
                    background,
                    useFlexRow && 'flex items-center',
                    useFlexRow && !isPinned && 'mb-8',
                    isLeft ? 'text-left' : 'text-center',
                    isPinned
                        ? 'fixed w-full pt-2 px-4 md:flex md:items-center md:justify-center md:pt-4 bg-gray-lightest'
                        : isLeft
                          ? 'md:pl-0 p-0'
                          : 'p-4'
                )}
                style={{ bottom: 0, left: 0 }}
            >
                <p
                    className={classNames(
                        'font-semibold',
                        !useFlexRow || isPinned ? 'mb-4' : 'mr-4',
                        titleSizes,
                        isPinned
                            ? 'hidden md:block md:mr-6 md:mt-3 text-black'
                            : `text-${titleColor}`
                    )}
                >
                    {title}
                </p>
                <div
                    className={classNames(
                        !isPinned && zipContainerClasses,
                        isEnrollmentPage ? 'w-100' : !isPinned && 'my-4 md:my-0'
                    )}
                >
                    <ZipForm
                        buttonWaLink={waLink}
                        qaValue="FindPlan"
                        opts={{
                            useWideLayout: true,
                            dialogPosition: '-top-16',
                            useWhiteText: !isPinned && titleColor === 'white',
                            isHorizontalOnMobile: isPinned,
                            setConditionalClasses: (showCounty: boolean) =>
                                showCounty && isPinned && 'mb-4 md:mb-0',
                        }}
                    />
                </div>
                {isPinned && (
                    <button
                        className="flex items-center absolute top-0 right-0 pr-2 md:pt-2 md:pr-3 lg:pt-3 lg:pr-4"
                        type="button"
                        data-wa-link="Close ZIP CTA"
                        onClick={handleClose}
                    >
                        <span className="text-sm mr-1">Close</span>
                        <CloseIcon data-qa="close-cta-sticky" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default FindPlan;
